import React, { useEffect, useState } from 'react';
import NewGameState from '../../../_types/NewGameState';
import RoundView from './Round.view';
// import * as yup from 'yup';
import { useGetUserInformationQuery } from '../../../../../../store/api/GraphQlQuery';
import { LOG, getLocalDate } from '../../../../../../common/Utilities';
import getKpis from '../../../../games-helpers/getKpis';
import RoundState from '../../../_types/RoundState';
import RewardType from '../../../_types/RewardType';
import getRewards from '../../../../games-helpers/getRewards';
import { isBefore, isEqual, addDays, addWeeks } from 'date-fns';
import RoundErrorMessagesState from '../../../_types/RoundErrorMessagesState';

interface Props {
  newGameState: NewGameState;
  onChangeNewGameState: (newGameState: NewGameState) => void;
  tabNo: number;
  setShouldDisableNextOrFinishButton: any;
  scrollToTop?: () => void;
}

const ROUND_INITIAL_ERRORS = {
  bracketName: '',
  startDate: '',
  endDate: '',
  actionCount: '',
  isActionCountCustom: '',
  KPIRuleID: '',
  tieKPIRuleID: '',
  useMultipleKPIs: '',
};

const RoundContainer: React.FC<Props> = ({
  newGameState,
  onChangeNewGameState,
  tabNo,
  setShouldDisableNextOrFinishButton,
  scrollToTop,
}) => {
  const roundValues = newGameState.bracket.roundValues[tabNo - 1];

  // Function to get values from previous round for copying
  const getPreviousRoundValues = () => {
    const previousRoundIndex = tabNo - 2; // tabNo is 1-indexed
    if (
      previousRoundIndex >= 0 &&
      newGameState.bracket.roundValues[previousRoundIndex]
    ) {
      return newGameState.bracket.roundValues[previousRoundIndex];
    }
    return null;
  };

  // Function to calculate dates based on previous round (mobile app logic)
  const calculateDatesFromPrevious = (previousRound: RoundState) => {
    if (!previousRound?.endDate) {
      return { startDate: '', endDate: '' };
    }

    try {
      // Add 2 days to the previous round's end date for the new start date
      const previousEndDate = new Date(previousRound.endDate);
      const newStartDate = addDays(previousEndDate, 2);
      newStartDate.setHours(0, 0, 0, 0); // Start of day

      // Calculate end date (add 1 week from start date as default)
      const newEndDate = addWeeks(newStartDate, 1);
      newEndDate.setHours(0, 0, 0, 0); // Start of day

      // Return dates as full ISO datetime strings to match form components
      return {
        startDate: newStartDate.toISOString(),
        endDate: newEndDate.toISOString(),
      };
    } catch (error) {
      console.error('Error calculating dates:', error);
      return { startDate: '', endDate: '' };
    }
  };

  // Helper function to handle copying values, considering empty strings as "not set"
  const copyIfEmpty = (
    currentValue: any,
    previousValue: any,
    shouldCopy: boolean
  ) => {
    // If current value is truthy and not just an empty string, keep it
    if (currentValue && currentValue !== '') {
      return currentValue;
    }
    // Otherwise, copy from previous if we should copy
    return shouldCopy ? previousValue ?? '' : '';
  };

  const hasConfiguredRewards = (round: any) => {
    if (!round) return false;

    // Check if reward settings have been meaningfully configured by the user
    // Don't count basic currency/type changes as "configured" since those can be defaults
    const hasRewardCounts =
      (round.rewardCount1 && round.rewardCount1 !== '') ||
      (round.rewardCount2 && round.rewardCount2 !== '') ||
      (round.rewardCount3 && round.rewardCount3 !== '') ||
      (round.rewardCountP && round.rewardCountP !== '') ||
      (round.rewardLimit && round.rewardLimit !== '');

    const hasCustomRewards =
      round.showCustomReward1 === true ||
      round.showCustomReward2 === true ||
      round.showCustomReward3 === true;

    const hasRewardLimit = round.rewardLimit && round.rewardLimit !== '';

    // Check for actual rewards being set (not just default empty values)
    const hasSpecificRewards =
      (round.rewardID1 && round.rewardID1 !== '') ||
      (round.rewardName1 && round.rewardName1 !== '') ||
      (round.rewardID2 && round.rewardID2 !== '') ||
      (round.rewardName2 && round.rewardName2 !== '') ||
      (round.rewardID3 && round.rewardID3 !== '');

    const result =
      hasRewardCounts ||
      hasCustomRewards ||
      hasRewardLimit ||
      hasSpecificRewards;

    return result;
  };

  // Initialize round state with copying logic
  const initializeRoundState = (): RoundState => {
    const previousRound = getPreviousRoundValues();

    // Check if should copy from previous round:
    // 1. Current round doesn't have KPI OR
    // 2. Previous round has configured rewards but current doesn't
    const shouldCopyKPIs =
      (!roundValues?.KPIRuleID || roundValues?.KPIRuleID === '') &&
      previousRound?.KPIRuleID &&
      previousRound?.KPIRuleID !== '';

    const shouldCopyRewards =
      previousRound &&
      hasConfiguredRewards(previousRound) &&
      !hasConfiguredRewards(roundValues);

    const shouldCopyFromPrevious = shouldCopyKPIs || shouldCopyRewards;

    // Calculate dates from previous round if available
    const calculatedDates = previousRound
      ? calculateDatesFromPrevious(previousRound)
      : { startDate: '', endDate: '' };

    // For Round 1, set today's date as default start date only - user must pick end date
    let defaultStartDate = '';
    let defaultEndDate = '';
    if (tabNo === 1 && !roundValues?.startDate) {
      // Format as full ISO datetime strings to match form components
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Start of day
      defaultStartDate = today.toISOString();
      // Leave endDate empty for Round 1 - user must pick it
      defaultEndDate = roundValues?.endDate ?? '';
    } else {
      defaultStartDate = roundValues?.startDate ?? calculatedDates.startDate;
      defaultEndDate = roundValues?.endDate ?? calculatedDates.endDate;
    }

    const finalState = {
      bracketName: roundValues?.bracketName ?? `Round ${tabNo}`,
      // Use calculated dates if current round doesn't have dates set, or today's date for Round 1
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      // Copy from previous round if current round is empty but previous has values
      actionCount:
        copyIfEmpty(
          roundValues?.actionCount,
          previousRound?.actionCount,
          shouldCopyFromPrevious
        ) || '0',
      isActionCountCustom:
        copyIfEmpty(
          roundValues?.isActionCountCustom,
          previousRound?.isActionCountCustom,
          shouldCopyFromPrevious
        ) ?? false,
      KPIRuleID: copyIfEmpty(
        roundValues?.KPIRuleID,
        previousRound?.KPIRuleID,
        shouldCopyFromPrevious
      ),
      KPIRuleName: copyIfEmpty(
        roundValues?.KPIRuleName,
        previousRound?.KPIRuleName,
        shouldCopyFromPrevious
      ),
      tieKPIRuleID: copyIfEmpty(
        roundValues?.tieKPIRuleID,
        previousRound?.tieKPIRuleID,
        shouldCopyFromPrevious
      ),
      tieKPIRuleName: copyIfEmpty(
        roundValues?.tieKPIRuleName,
        previousRound?.tieKPIRuleName,
        shouldCopyFromPrevious
      ),
      useMultipleKPIs:
        copyIfEmpty(
          roundValues?.useMultipleKPIs,
          previousRound?.useMultipleKPIs,
          shouldCopyFromPrevious
        ) ?? false,
      actionCountType:
        copyIfEmpty(
          roundValues?.actionCountType,
          previousRound?.actionCountType,
          shouldCopyFromPrevious
        ) || 'count',
      actionCountTypeNum:
        copyIfEmpty(
          roundValues?.actionCountTypeNum,
          previousRound?.actionCountTypeNum,
          shouldCopyFromPrevious
        ) ?? 0,
      // Copy ALL reward settings from previous round
      rewardDisposition:
        copyIfEmpty(
          roundValues?.rewardDisposition,
          previousRound?.rewardDisposition,
          shouldCopyFromPrevious
        ) || 'winner',
      rewardWhen:
        copyIfEmpty(
          roundValues?.rewardWhen,
          previousRound?.rewardWhen,
          shouldCopyFromPrevious
        ) || 'mission',
      rewardType:
        copyIfEmpty(
          roundValues?.rewardType,
          previousRound?.rewardType,
          shouldCopyFromPrevious
        ) || 'mission_reward',
      rewardCurrency:
        copyIfEmpty(
          roundValues?.rewardCurrency,
          previousRound?.rewardCurrency,
          shouldCopyFromPrevious
        ) || 'reward',
      // Copy reward counts from previous round
      rewardCount1: copyIfEmpty(
        roundValues?.rewardCount1,
        previousRound?.rewardCount1,
        shouldCopyFromPrevious
      ),
      rewardCount1t: copyIfEmpty(
        roundValues?.rewardCount1t,
        previousRound?.rewardCount1t,
        shouldCopyFromPrevious
      ),
      rewardCount2: copyIfEmpty(
        roundValues?.rewardCount2,
        previousRound?.rewardCount2,
        shouldCopyFromPrevious
      ),
      rewardCount2t: copyIfEmpty(
        roundValues?.rewardCount2t,
        previousRound?.rewardCount2t,
        shouldCopyFromPrevious
      ),
      rewardCount3: copyIfEmpty(
        roundValues?.rewardCount3,
        previousRound?.rewardCount3,
        shouldCopyFromPrevious
      ),
      rewardCount3t: copyIfEmpty(
        roundValues?.rewardCount3t,
        previousRound?.rewardCount3t,
        shouldCopyFromPrevious
      ),
      rewardCountP: copyIfEmpty(
        roundValues?.rewardCountP,
        previousRound?.rewardCountP,
        shouldCopyFromPrevious
      ),
      rewardLimit: copyIfEmpty(
        roundValues?.rewardLimit,
        previousRound?.rewardLimit,
        shouldCopyFromPrevious
      ),
      // Copy custom reward flags
      showCustomReward1:
        copyIfEmpty(
          roundValues?.showCustomReward1,
          previousRound?.showCustomReward1,
          shouldCopyFromPrevious
        ) ?? false,
      showCustomReward2:
        copyIfEmpty(
          roundValues?.showCustomReward2,
          previousRound?.showCustomReward2,
          shouldCopyFromPrevious
        ) ?? false,
      showCustomReward3:
        copyIfEmpty(
          roundValues?.showCustomReward3,
          previousRound?.showCustomReward3,
          shouldCopyFromPrevious
        ) ?? false,
      // NOW COPY reward IDs and names from previous round when conditions are met
      rewardID1: copyIfEmpty(
        roundValues?.rewardID1,
        previousRound?.rewardID1,
        shouldCopyFromPrevious
      ),
      rewardName1: copyIfEmpty(
        roundValues?.rewardName1,
        previousRound?.rewardName1,
        shouldCopyFromPrevious
      ),
      rewardID2: copyIfEmpty(
        roundValues?.rewardID2,
        previousRound?.rewardID2,
        shouldCopyFromPrevious
      ),
      rewardName2: copyIfEmpty(
        roundValues?.rewardName2,
        previousRound?.rewardName2,
        shouldCopyFromPrevious
      ),
      rewardID3: copyIfEmpty(
        roundValues?.rewardID3,
        previousRound?.rewardID3,
        shouldCopyFromPrevious
      ),
      rewardName3: copyIfEmpty(
        roundValues?.rewardName3,
        previousRound?.rewardName3,
        shouldCopyFromPrevious
      ),
      rewardIDp: copyIfEmpty(
        roundValues?.rewardIDp,
        previousRound?.rewardIDp,
        shouldCopyFromPrevious
      ),
      rewardNameP: copyIfEmpty(
        roundValues?.rewardNameP,
        previousRound?.rewardNameP,
        shouldCopyFromPrevious
      ),
      rewardID1t: copyIfEmpty(
        roundValues?.rewardID1t,
        previousRound?.rewardID1t,
        shouldCopyFromPrevious
      ),
      rewardName1t: copyIfEmpty(
        roundValues?.rewardName1t,
        previousRound?.rewardName1t,
        shouldCopyFromPrevious
      ),
      rewardID2t: copyIfEmpty(
        roundValues?.rewardID2t,
        previousRound?.rewardID2t,
        shouldCopyFromPrevious
      ),
      rewardName2t: copyIfEmpty(
        roundValues?.rewardName2t,
        previousRound?.rewardName2t,
        shouldCopyFromPrevious
      ),
      rewardID3t: copyIfEmpty(
        roundValues?.rewardID3t,
        previousRound?.rewardID3t,
        shouldCopyFromPrevious
      ),
      rewardName3t: copyIfEmpty(
        roundValues?.rewardName3t,
        previousRound?.rewardName3t,
        shouldCopyFromPrevious
      ),
      rewardIDpt: copyIfEmpty(
        roundValues?.rewardIDpt,
        previousRound?.rewardIDpt,
        shouldCopyFromPrevious
      ),
      rewardNamePt: copyIfEmpty(
        roundValues?.rewardNamePt,
        previousRound?.rewardNamePt,
        shouldCopyFromPrevious
      ),
      isInitialized: roundValues?.isInitialized ?? true,
      // Copy mission rules from previous round if available
      missionRules:
        roundValues?.missionRules?.length > 0
          ? roundValues.missionRules
          : shouldCopyFromPrevious && previousRound?.missionRules
          ? [...previousRound.missionRules]
          : [],
      rules:
        roundValues?.rules?.length > 0
          ? roundValues.rules
          : shouldCopyFromPrevious && previousRound?.rules
          ? [...previousRound.rules]
          : [],
      // Continue with other fields...
      currentMissionId: roundValues?.currentMissionId ?? '',
      id: roundValues?.id ?? '',
      type: roundValues?.type ?? '',
      name: roundValues?.name ?? '',
      description: roundValues?.description ?? '',
      search: roundValues?.search ?? '',
      imageName: roundValues?.imageName ?? '',
      imageType: roundValues?.imageType ?? '',
      createMission: roundValues?.createMission ?? false,
      pickAssignee: roundValues?.pickAssignee ?? false,
      uploadloadingPicture: roundValues?.uploadloadingPicture ?? false,
      uploadPictureTitle: roundValues?.uploadPictureTitle ?? '',
      parentID: roundValues?.parentID ?? '',
      parentName: roundValues?.parentName ?? '',
      scope: roundValues?.scope ?? '',
      assigneeID: roundValues?.assigneeID ?? '',
      assigneeName: roundValues?.assigneeName ?? '',
      ownerID: roundValues?.ownerID ?? '',
      ownerType: roundValues?.ownerType ?? '',
      questType: roundValues?.questType ?? '',
      questOrder: roundValues?.questOrder ?? '',
      launchDate: roundValues?.launchDate ?? '',
      location: roundValues?.location ?? '',
      action: roundValues?.action ?? '',
      actionPastTense: roundValues?.actionPastTense ?? '',
      actionTypePlural: roundValues?.actionTypePlural ?? '',
      actionType: roundValues?.actionType ?? '',
      missionRewardID1: roundValues?.missionRewardID1 ?? '',
      missionRewardID2: roundValues?.missionRewardID2 ?? '',
      missionRewardID3: roundValues?.missionRewardID3 ?? '',
      missionRewardIDp: roundValues?.missionRewardIDp ?? '',
      missionRewardID1t: roundValues?.missionRewardID1t ?? '',
      missionRewardID2t: roundValues?.missionRewardID2t ?? '',
      missionRewardID3t: roundValues?.missionRewardID3t ?? '',
      missionRewardIDpt: roundValues?.missionRewardIDpt ?? '',
      tags: roundValues?.tags ?? [],
      orgID: roundValues?.orgID ?? 'dev',
      autoAssign: roundValues?.autoAssign ?? true,
      copiedMissionID: roundValues?.copiedMissionID ?? '',
      copiedMissionName: roundValues?.copiedMissionName ?? '',
      copyMission: roundValues?.copyMission ?? false,
      trophies: roundValues?.trophies ?? [],
      isDeleted: roundValues?.isDeleted ?? false,
      useDealStage: roundValues?.useDealStage ?? true,
      categoryID: roundValues?.categoryID ?? '',
      flexFields: roundValues?.flexFields ?? [],
      useStepCounter: roundValues?.useStepCounter ?? false,
      assignToChildCircles: roundValues?.assignToChildCircles ?? true,
      isCircleOnly: roundValues?.isCircleOnly ?? false,
      isPublic: roundValues?.isPublic ?? false,
      managerPlay: roundValues?.managerPlay ?? true,
      contestType: roundValues?.contestType ?? 'bracket_manual',
      bracket: roundValues?.bracket ?? [],
      minGoal: roundValues?.minGoal ?? '',
      useAutoGoal: roundValues?.useAutoGoal ?? false,
      autoGoalTimeframe: roundValues?.autoGoalTimeframe ?? 'W',
      autoGoalTimeframeCount: roundValues?.autoGoalTimeframeCount ?? '4',
      autoGoalFactor: roundValues?.autoGoalFactor ?? '100',
      minAutoGoal: roundValues?.minAutoGoal ?? '1.0',
      roundGoalPrecision: roundValues?.roundGoalPrecision ?? '0',
      useAutoCreate: roundValues?.useAutoCreate ?? false,
      autoCreateTimeframe: roundValues?.autoCreateTimeframe ?? 'W',
      autoCreateTimeframeCount: roundValues?.autoCreateTimeframeCount ?? '1',
      autoCreateStartDate: roundValues?.autoCreateStartDate ?? '',
      autoCreateEndDate: roundValues?.autoCreateEndDate ?? '',
      nextMissionID: roundValues?.nextMissionID ?? '',
      advanceType: roundValues?.advanceType ?? 'never',
      topNumberAdvance: roundValues?.topNumberAdvance ?? 0,
      roundValues: roundValues?.roundValues ?? [],
      currentTotalCompetitors: roundValues?.currentTotalCompetitors ?? 3,
      currentRounds: roundValues?.currentRounds ?? 2,
      currentRoundValues: roundValues?.currentRoundValues ?? [],
      showCustomGoal: roundValues?.showCustomGoal ?? false,
    };

    return finalState;
  };

  const [roundState, setRoundState] = useState<RoundState>(
    initializeRoundState()
  );

  // Sync initialized state to parent on mount (especially important for Round 1 default date)
  useEffect(() => {
    const initialState = initializeRoundState();
    // Only sync if parent state doesn't already have values or if we have important defaults
    const parentRoundData = newGameState.bracket?.roundValues?.[tabNo - 1];

    // Sync if parent is missing data that we have in our initialized state
    const shouldSync =
      !parentRoundData ||
      (!parentRoundData.startDate && initialState.startDate) ||
      (!parentRoundData.endDate && initialState.endDate) ||
      (!parentRoundData.bracketName && initialState.bracketName);

    if (shouldSync) {
      const newGameStateCopy = { ...newGameState };

      // Ensure roundValues array exists and has space for this round
      while (newGameStateCopy.bracket.roundValues.length < tabNo) {
        newGameStateCopy.bracket.roundValues.push({} as RoundState);
      }

      // Update parent state with our initialized values
      newGameStateCopy.bracket.roundValues[tabNo - 1] = {
        ...newGameStateCopy.bracket.roundValues[tabNo - 1],
        ...initialState,
      };

      onChangeNewGameState(newGameStateCopy);
    }
  }, []); // Run once on mount

  const { data: userData, isLoading } = useGetUserInformationQuery({});
  const [kpis, setKpis] = useState<
    { label: string; value: string; rule: any }[]
  >([]);
  const [rewards, setRewards] = useState<RewardType[]>([]);

  // Function to ensure the next round has copied values from current round
  const ensureNextRoundCopying = () => {
    const nextRoundIndex = tabNo; // Next round (tabNo is current round, 1-indexed)
    const currentRoundData = roundState;

    // Check if next round exists and doesn't have KPI values yet
    if (nextRoundIndex < newGameState.totalRounds) {
      const newGameStateCopy = { ...newGameState };

      // Ensure the next round exists in the array
      while (newGameStateCopy.bracket.roundValues.length <= nextRoundIndex) {
        newGameStateCopy.bracket.roundValues.push({} as RoundState);
      }

      const nextRound = newGameStateCopy.bracket.roundValues[nextRoundIndex];

      // Check if should copy: next round needs KPI OR current round has configured rewards but next doesn't
      const nextRoundNeedsKPI =
        !nextRound.KPIRuleID || nextRound.KPIRuleID === '';
      const currentRoundHasKPI =
        currentRoundData.KPIRuleID && currentRoundData.KPIRuleID !== '';

      const shouldCopyKPIs = nextRoundNeedsKPI && currentRoundHasKPI;
      const shouldCopyRewards =
        hasConfiguredRewards(currentRoundData) &&
        !hasConfiguredRewards(nextRound);

      if (shouldCopyKPIs || shouldCopyRewards) {
        // Calculate dates for next round
        const calculatedDates = calculateDatesFromPrevious(currentRoundData);

        const updatedNextRound = {
          ...nextRound,
          bracketName: `Round ${nextRoundIndex + 1}`,
          // Copy dates from calculation
          startDate: calculatedDates.startDate,
          endDate: calculatedDates.endDate,
          // Copy KPI settings
          KPIRuleID: currentRoundData.KPIRuleID,
          KPIRuleName: currentRoundData.KPIRuleName,
          tieKPIRuleID: currentRoundData.tieKPIRuleID,
          tieKPIRuleName: currentRoundData.tieKPIRuleName,
          useMultipleKPIs: currentRoundData.useMultipleKPIs,
          // Copy points settings
          actionCount: currentRoundData.actionCount,
          isActionCountCustom: currentRoundData.isActionCountCustom,
          actionCountType: currentRoundData.actionCountType,
          actionCountTypeNum: currentRoundData.actionCountTypeNum,
          // Copy ALL reward settings
          rewardDisposition: currentRoundData.rewardDisposition,
          rewardWhen: currentRoundData.rewardWhen,
          rewardType: currentRoundData.rewardType,
          rewardCurrency: currentRoundData.rewardCurrency,
          rewardCount1: currentRoundData.rewardCount1,
          rewardCount1t: currentRoundData.rewardCount1t,
          rewardCount2: currentRoundData.rewardCount2,
          rewardCount2t: currentRoundData.rewardCount2t,
          rewardCount3: currentRoundData.rewardCount3,
          rewardCount3t: currentRoundData.rewardCount3t,
          rewardCountP: currentRoundData.rewardCountP,
          rewardLimit: currentRoundData.rewardLimit,
          showCustomReward1: currentRoundData.showCustomReward1,
          showCustomReward2: currentRoundData.showCustomReward2,
          showCustomReward3: currentRoundData.showCustomReward3,
          // Copy mission rules
          missionRules: currentRoundData.missionRules
            ? [...currentRoundData.missionRules]
            : [],
          rules: currentRoundData.rules ? [...currentRoundData.rules] : [],
        };

        newGameStateCopy.bracket.roundValues[nextRoundIndex] = updatedNextRound;
        onChangeNewGameState(newGameStateCopy);
      }
    }
  };

  // Initialize error messages with required field errors if fields are empty
  const getInitialErrors = () => {
    const initialErrors = { ...ROUND_INITIAL_ERRORS };

    // Only show errors for empty required fields
    if (!roundValues?.bracketName) {
      initialErrors.bracketName = 'Round name is required';
    }

    if (!roundValues?.KPIRuleID) {
      initialErrors.KPIRuleID = 'KPI is required';
    }

    if (!roundValues?.startDate) {
      initialErrors.startDate = 'Start date is required';
    }

    if (!roundValues?.endDate) {
      initialErrors.endDate = 'End date is required';
    }

    return initialErrors;
  };

  const [errorMessages, setErrorMessages] = useState<RoundErrorMessagesState>(
    getInitialErrors()
  );

  const onChangeRoundState = (newState: RoundState) => {
    setRoundState(newState);
  };

  const isErrorPresent = Object.values(errorMessages).some(
    (message) => message !== ''
  );
  const [shouldDisableNextButton, setShouldDisableNextButton] =
    useState(isErrorPresent);

  const onNextRound = () => {
    // if (shouldDisableNextButton) return;

    // Create a copy of initial errors
    const errors = { ...ROUND_INITIAL_ERRORS };
    let hasErrors = false;

    // Validate that all required fields are filled
    if (!roundState.bracketName) {
      errors.bracketName = 'Round name is required';
      hasErrors = true;
    }

    if (!roundState.KPIRuleID) {
      errors.KPIRuleID = 'KPI is required';
      hasErrors = true;
    }

    if (!roundState.startDate) {
      errors.startDate = 'Start date is required';
      hasErrors = true;
    }

    if (!roundState.endDate) {
      errors.endDate = 'End date is required';
      hasErrors = true;
    }

    // If there are errors, update error messages state and return
    if (hasErrors) {
      setErrorMessages(errors);
      setShouldDisableNextButton(true);
      return;
    }

    // Ensure next round gets copied values
    ensureNextRoundCopying();

    // Navigate to next round
    onChangeNewGameState({
      ...newGameState,
      currentRound: newGameState.currentRound + 1,
    });

    // Scroll to top using the passed function
    if (scrollToTop) {
      setTimeout(scrollToTop, 50);
    }
  };

  const shouldShowNextRoundButton = (): boolean => {
    return tabNo < newGameState.totalRounds;
  };

  const validate = () => {
    const errors = { ...errorMessages };

    // Required field validations
    if (!roundState.bracketName) {
      errors.bracketName = 'Round name is required';
    } else {
      errors.bracketName = '';
    }

    if (!roundState.KPIRuleID) {
      errors.KPIRuleID = 'KPI is required';
    } else {
      errors.KPIRuleID = '';
    }

    if (!roundState.startDate) {
      errors.startDate = 'Start date is required';
    }

    if (!roundState.endDate) {
      errors.endDate = 'End date is required';
    }

    // Start Date validation
    const previousRoundValues = newGameState.bracket.roundValues[tabNo - 2];
    if (
      roundState.startDate &&
      previousRoundValues?.endDate &&
      isBefore(
        new Date(roundState.startDate),
        new Date(previousRoundValues?.endDate)
      )
    ) {
      errors.startDate = `Start Date cannot be earlier than the previous round's end date`;
      setShouldDisableNextOrFinishButton(true);
    } else if (!roundState.startDate) {
      errors.startDate = 'Start date is required';
      setShouldDisableNextOrFinishButton(true);
    } else {
      errors.startDate = '';
      setShouldDisableNextOrFinishButton(false);
    }

    // End Date validation
    if (
      roundState.endDate &&
      roundState.startDate &&
      isBefore(new Date(roundState.endDate), new Date(roundState.startDate))
    ) {
      errors.endDate = 'End Date should not be earlier than Start Date';
      setShouldDisableNextOrFinishButton(true);
    } else if (!roundState.endDate) {
      errors.endDate = 'End date is required';
      setShouldDisableNextOrFinishButton(true);
    } else {
      errors.endDate = '';
      setShouldDisableNextOrFinishButton(false);
    }

    // Start Date and End Date validation, check if both are the same
    if (
      roundState.startDate &&
      roundState.endDate &&
      isEqual(new Date(roundState.startDate), new Date(roundState.endDate))
    ) {
      errors.endDate = 'Start Date and End Date should not be the same';
      setShouldDisableNextOrFinishButton(true);
    }

    // Additional validation check for last round
    if (tabNo === newGameState.totalRounds && !roundState.KPIRuleID) {
      // Last round validation will be handled by the standard KPI validation above
    }

    return errors;
  };

  useEffect(() => {
    const fetchKpis = async () => {
      if (isLoading || !userData) {
        return;
      }

      try {
        const kpis = await getKpis({
          me: userData.me,
        });

        setKpis(kpis);
      } catch (error) {
        LOG.error(error);
      }
    };

    const fetchRewards = async () => {
      if (isLoading || !userData) {
        return;
      }

      const rewards = await getRewards({
        endAfter: getLocalDate(),
        orgID: userData.me.orgID,
        startBefore: getLocalDate(),
        userID: userData.me.id,
      });
      setRewards(rewards);
    };

    fetchKpis();
    fetchRewards();
  }, [userData, isLoading]);

  useEffect(() => {
    const errors = validate();
    const isErrorPresent = Object.values(errors).some(
      (message) => message !== ''
    );

    setShouldDisableNextButton(isErrorPresent);
    setErrorMessages(errors);
  }, [roundState]);

  // Effect to update parent state when navigating to rounds that need copying
  useEffect(() => {
    const previousRound = getPreviousRoundValues();
    // Check if should copy from previous round:
    // 1. Current round doesn't have KPI OR
    // 2. Previous round has configured rewards but current doesn't
    const shouldCopyKPIs =
      (!roundValues?.KPIRuleID || roundValues?.KPIRuleID === '') &&
      previousRound?.KPIRuleID &&
      previousRound?.KPIRuleID !== '';

    const shouldCopyRewards =
      previousRound &&
      hasConfiguredRewards(previousRound) &&
      !hasConfiguredRewards(roundValues);

    // If we should copy from previous but haven't yet, update the parent state
    if (shouldCopyKPIs || shouldCopyRewards) {
      const newGameStateCopy = { ...newGameState };

      // Ensure roundValues array exists
      if (!newGameStateCopy.bracket.roundValues[tabNo - 1]) {
        newGameStateCopy.bracket.roundValues[tabNo - 1] = {} as RoundState;
      }

      // Calculate dates from previous round
      const calculatedDates = calculateDatesFromPrevious(previousRound);

      // Copy the relevant values to parent state
      const updatedRound = {
        ...newGameStateCopy.bracket.roundValues[tabNo - 1],
        bracketName: `Round ${tabNo}`, // Keep the round name
        // Copy dates from calculation
        startDate: calculatedDates.startDate,
        endDate: calculatedDates.endDate,
        // Copy KPI settings
        KPIRuleID: previousRound.KPIRuleID,
        KPIRuleName: previousRound.KPIRuleName,
        tieKPIRuleID: previousRound.tieKPIRuleID,
        tieKPIRuleName: previousRound.tieKPIRuleName,
        useMultipleKPIs: previousRound.useMultipleKPIs,
        // Copy points settings
        actionCount: previousRound.actionCount,
        isActionCountCustom: previousRound.isActionCountCustom,
        actionCountType: previousRound.actionCountType,
        actionCountTypeNum: previousRound.actionCountTypeNum,
        // Copy ALL reward settings
        rewardDisposition: previousRound.rewardDisposition,
        rewardWhen: previousRound.rewardWhen,
        rewardType: previousRound.rewardType,
        rewardCurrency: previousRound.rewardCurrency,
        rewardCount1: previousRound.rewardCount1,
        rewardCount1t: previousRound.rewardCount1t,
        rewardCount2: previousRound.rewardCount2,
        rewardCount2t: previousRound.rewardCount2t,
        rewardCount3: previousRound.rewardCount3,
        rewardCount3t: previousRound.rewardCount3t,
        rewardCountP: previousRound.rewardCountP,
        rewardLimit: previousRound.rewardLimit,
        showCustomReward1: previousRound.showCustomReward1,
        showCustomReward2: previousRound.showCustomReward2,
        showCustomReward3: previousRound.showCustomReward3,
        // Copy mission rules
        missionRules: previousRound.missionRules
          ? [...previousRound.missionRules]
          : [],
        rules: previousRound.rules ? [...previousRound.rules] : [],
      };

      newGameStateCopy.bracket.roundValues[tabNo - 1] = updatedRound;
      onChangeNewGameState(newGameStateCopy);
    }
  }, [tabNo, newGameState.bracket.roundValues]);

  // Sync local roundState with parent state when parent state gets updated with copied values.
  useEffect(() => {
    const parentRoundData = newGameState.bracket?.roundValues?.[tabNo - 1];
    if (parentRoundData && parentRoundData !== roundValues) {
      // If parent has copied rewards but local state doesn't, sync them
      if (
        hasConfiguredRewards(parentRoundData) &&
        !hasConfiguredRewards(roundState)
      ) {
        setRoundState((prev) => ({
          ...prev,
          ...parentRoundData,
        }));
      }
    }
  }, [newGameState.bracket?.roundValues, tabNo]);

  // Monitor for changes in current round that should trigger copying to next round
  const currentRound = newGameState.bracket?.roundValues?.[tabNo - 1];

  useEffect(() => {
    ensureNextRoundCopying();
  }, [
    currentRound?.KPIRuleID,
    currentRound?.rewardType,
    currentRound?.rewardCurrency,
    currentRound?.rewardWhen,
    currentRound?.actionCount,
    // Monitor all reward-related fields that could trigger copying
    currentRound?.rewardCount1,
    currentRound?.rewardCount2,
    currentRound?.rewardCount3,
    currentRound?.rewardCountP,
    currentRound?.showCustomReward1,
    currentRound?.showCustomReward2,
    currentRound?.showCustomReward3,
    currentRound?.rewardLimit,
  ]);

  if (isLoading || !userData) {
    return null;
  }

  return (
    <RoundView
      tabNo={tabNo}
      kpis={kpis}
      roundState={roundState}
      newGameState={newGameState}
      onChangeRoundState={onChangeRoundState}
      onChangeNewGameState={onChangeNewGameState}
      shouldDisableNextButton={shouldDisableNextButton}
      rewards={rewards}
      onNextRound={onNextRound}
      shouldShowNextRoundButton={shouldShowNextRoundButton()}
      errorMessages={errorMessages}
    />
  );
};

export default RoundContainer;
